import {
  PasswordLoginOptions,
  MfaLoginOptions,
  RememberedDevice
} from '../auth/common';
import {
  ClientMetadata,
  CognitoUser,
  CognitoUserSession,
  ISignUpResult
} from 'amazon-cognito-identity-js';

/**
 * This is the data used during the authentication process.
 */
export interface AuthData {
  /** The roles of the currently logged in user */
  roles?: string[];

  /**
   * An access token for the logged in user to be sent in requests to verify
   * that they should be able to access those resources
   */
  accessToken?: string;

  /**
   * An ID token for associating with Built API
   */
  idToken?: string;

  /**
   * The token used to refresh the above access and ID tokens
   */
  refreshToken?: string;

  /** A token that validates the current user session */
  sessionToken?: string;

  /** A token for registration */
  registrationToken?: string;

  /** The username of the currently logged in user */
  username?: string;

  /** Whether the logged in user's email address has been verified */
  emailVerified?: boolean;

  /** The email address of the currently logged in user */
  email?: string;

  /** Whether the currently logged in user needs to reset their password */
  newPasswordRequired?: boolean;

  /** Whether the currently logged in user required MFA authentication */
  needsMfa?: string;

  /** Foo */
  // tslint:disable-next-line:no-any
  mfaParams?: any;

  /** Foo */
  // tslint:disable-next-line:no-any
  mfaRequired?: boolean;

  /** Foo */
  // tslint:disable-next-line:no-any
  mfaCompleted?: boolean;

  /** Any additional attributes of the newly logged in user */
  data?: UserData;

  /** Has the user agreed to the latest ToS */
  agreedToTos?: boolean;

  /** Flag for federated users to avoid setting up password or MFA. */
  federated?: boolean;

  /** Was the user already signed in and ventured back to login? */
  welcomeBack?: boolean;

  /** Flag for determining if a user came from IMA */
  isImaUser?: boolean;

  /** Did they originate from Nativ? (Deal) */
  isNativUser?: boolean;

  /** Flag used to determine if the user verified their email address */
  emailVerification?: boolean;

  /** Flag indicating we're in maintenance mode */
  maintenanceMode?: boolean;

  /** Logout status set by console */
  logoutStatus?: string;

  /**
   * User-provided email address. This is either set when user provides
   * email input or through the email query string
   */
  userProvidedEmail?: string;

  /** For SSO users, the client id is derived during the redirect from Cognito */
  ssoClientId?: string;

  /** If a user is registering, origin must be provided to direct proper flow. */
  origin?: UserOrigin;

  /**If a redirect url is provided in the query params, we set it here to allow override of default redirect location.  */
  redirectUrl?: URL;
}

/**
 * This is the data used during the registration process.
 */
export interface RegistrationData {
  userId: string;
  registrationToken: string;
  [name: string]: string;
}
/**
 * Origins of users being migrated, to direct proper flow to Cognito, BAPI & AuthService
 */
export enum UserOrigin {
  LWIO = 'LWIO',
  OWNR = 'OWNR',
  BAPI = 'BAPI',
  DEAL = 'DEAL'
}

export interface UserData {
  /** Any miscellaneous attributes of the currently logged in user */
  attributes: { [name: string]: string | number | boolean };

  /** The user's MFA settings */
  mfa: string[];
}

export enum LoginExceptions {
  // technically only available in password reset, but since the
  // password reset also makes use of these login exceptions we'll
  // keep it here for easier use
  LimitExceededException = 'LimitExceededException',
  // Auth exception from the mockCognito module
  MockCognitoException = 'MockCognitoException',
  // Cognito exception when a user's credentials are not correct;
  // this can also be overloaded with a temporary account lockout
  // after too many failed attempts
  NotAuthorizedException = 'NotAuthorizedException',
  // Cognito exception when a user's account is disabled until
  // a password reset is performed
  PasswordResetRequiredException = 'PasswordResetRequiredException',
  // MFA code mismatch
  CodeMismatchException = 'CodeMismatchException',
  // Cognito exception when user is not found in the pool
  UserNotFoundException = 'UserNotFoundException',
  // Cognito exeption when user has not confirmed their email before logging in
  UserNotConfirmedException = 'UserNotConfirmedException'
}

// substrings for NotAuthorizedException above
export enum NotAuthorizedSubstrings {
  // User has had too many unsuccessful login attempts
  AttemptsExceeded = 'Password attempts exceeded',
  // The user's account has been disabled in the pool
  AccountDisabled = 'User is disabled',
  // The user's didn't initialize their account in time and has expired
  AccountExpired = 'User account has expired',
  // Invalid email/password
  InvalidCredentials = 'Incorrect username or password',
  // This occurs when a user's account is expired and they attempt to
  // reset their password
  InvalidState = 'User password cannot be reset in the current state',
  // The user's session/token has expired
  SessionExpired = 'session is expired'
}

export enum AuthErrorType {
  ACCOUNT_DISABLED = 'AccountDisabledError',
  ACCOUNT_EXPIRED = 'AccountExpiredError',
  ATTEMPTS_EXCEEDED_ERROR = 'AttemptsExceededError',
  EMAIL_VERIFICATION_ERROR = 'EmailVerificationError',
  UNVERIFIED_USER_ERROR = 'UnverifiedUserError',
  INVALID_CREDENTIALS_ERROR = 'InvalidCredentialsError',
  LIMIT_EXCEEDED_ERROR = 'LimitExceededError',
  MFA_VERIFICATION_ERROR = 'MfaVerificationError',
  MFA_RESEND_SMS_ERROR = 'MfaResendSmsError',
  MOCK_COGNITO_ERROR = 'MockCognitoError',
  PASSWORD_RESET_REQUIRED_ERROR = 'PasswordResetRequiredError',
  REGISTRATION_ERROR = 'RegistrationError',
  SAML_RESPONSE_ERROR = 'SamlResponseError',
  TOKEN_EXPIRED = 'TokenExpired',
  NOT_AUTHORIZED_EXCEPTION = 'NotAuthorizedException',
  SESSION_EXPIRED = 'SessionExpired',
  UNKNOWN_ERROR = 'UnknownError'
}

export enum ValidationErrorType {
  VALIDATION_EMAIL_ERROR = 'VALIDATION_EMAIL_ERROR',
  VALIDATION_ALIAS_ERROR = 'VALIDATION_ALIAS_ERROR',
  VALIDATION_PASSWORD_ERROR = 'VALIDATION_PASSWORD_ERROR'
}

export enum DefaultApp {
  B4C = 'b4c',
  B4C_LWIO = 'b4c:lwio',
  B4C_OWNERS = 'b4c:owners',
  B4C_OWNERS_BORROWER = 'b4c:owners:borrower',
  B4C_PROJECT_VENDOR = 'b4c:project:vendor',
  B4C_VENDOR = 'b4c:vendor',
  DEAL = 'deal',
  NATIV = 'nativ',
  B4C_SELF_SERVE = 'selfserve',
  B4L = 'b4l:cre-lender'
}

export enum BapiDefaultRole {
  BUILDER_ADMINISTRATOR = 'builderAdministrator',
  BUILDER_READONLY = 'builderReadonly'
}

export interface AuthError {
  type?: AuthErrorType;
  message: string;
  attemptsRemaining?: number;
  title?: string;
}

export interface SamlTokens {
  access_token: string;
  id_token: string;
  refresh_token: string;
}

export interface ForgotPasswordResponse {
  CodeDeliveryDetails: {
    AttributeName: 'phone_number' | 'email';
    DeliveryMedium: 'SMS' | 'email';
    Destination: string;
  };
}

export interface AuthService {
  enableMfa(type: 'sms' | 'totp'): Promise<CognitoUser>;
  forgotPassword(email: string): Promise<ForgotPasswordResponse>;

  resetPassword(
    username: string,
    code: string,
    newPassword: string
  ): Promise<void>;

  getUserData(): Promise<UserData>;

  disableMfa(): Promise<void>;

  setInitialPassword(
    password: string,
    clientMetadata?: ClientMetadata
  ): Promise<AuthData>;

  login(options: PasswordLoginOptions | MfaLoginOptions): Promise<AuthData>;

  loginEmailVerification(
    previousEmail: string,
    password: string
  ): Promise<AuthData>;

  logout(): Promise<void>;

  setUserAttributes(attrs: { [name: string]: string }): Promise<void>;

  deleteUserAttribute(name: string): Promise<void>;

  verifyUserAttribute(name: string, code: string): Promise<void>;

  userSignUp(
    username: string,
    password: string,
    registrationToken: string,
    clientMetadata?: ClientMetadata
  ): Promise<ISignUpResult>;

  getUser(
    clientId?: string
  ): Promise<{ user?: CognitoUser; session?: CognitoUserSession }>;

  getAuthData(
    user?: CognitoUser,
    session?: CognitoUserSession
  ): Promise<AuthData | undefined>;

  resendSmsCode(): Promise<boolean>;

  verifySmsPhoneNumber(code: string): Promise<void>;

  verifySoftwareToken(
    code: string,
    authenticatorName?: string
  ): Promise<CognitoUser>;

  getTokenSecret(): Promise<string>;

  getRememberedDevices(): Promise<RememberedDevice[]>;

  forgetDevice(deviceId?: string): Promise<boolean>;

  exchangeAuthCode(
    clientId: string,
    authCode: string,
    redirectUrl: string
  ): Promise<AuthData | undefined>;
}

/**
 * Indicates whether a given authData object represents a user whose account
 * has completed onboarding.
 */
export function isUserSetup(authData: AuthData) {
  return !Boolean(authData.newPasswordRequired);
}

/**
 * Handles retrieving attributes from authorization data that uses Cognito's
 * schema.
 */
export function getAuthAttribute<T = string>(data: AuthData, name: string) {
  if (!data || !data.data || !data.data.attributes) {
    return undefined;
  }
  // tslint:disable-next-line:no-any
  return <T>(<any>data.data.attributes[name]);
}
